import type { CartLine } from "./cart.ts";

/**
 * The bills this till has open (`08-pos-terminal-and-qr.md` § A, steps 1 and 7).
 *
 * A cart used to be one screen's `useState`, which meant a seated table was a
 * fact known to one browser tab: the server heard about the bill when it was
 * paid, so every other terminal read the table as free for the whole of the
 * service. A bill now exists from the moment covers are confirmed, and the
 * floor is derived from it.
 *
 * **Lines still stay local until they are fired.** The bill opens empty and
 * the station gets the lines on "Send to station". That is not laziness about
 * syncing: `remove` and `setQuantity` only ever touch unfired lines, so a
 * cashier's mis-tap is a local correction. Send every tap to the server and
 * the same mis-tap becomes an audited void. It also gives the cart's own
 * "Ordered" / "Latest order" split its real meaning — what the server has,
 * and what it does not.
 *
 * Pure, and deliberately so: the till has no DOM test harness, so anything
 * that has to be right lives here rather than in a component.
 */

/**
 * A bill's handle for as long as it exists.
 *
 * Also the id of the operation that created it, hence the `Idempotency-Key`
 * the server stores — so a create that has to be replayed asks for the same
 * bill back. `orderId` is the server's own id, and is null only while the
 * create is still queued.
 */
export type BillKey = string;

export type Bill = {
  readonly key: BillKey;
  readonly orderId: string | null;
  readonly orderNo: string | null;
  readonly diningStyle: "dine_in" | "take_away";
  readonly table: { readonly id: string; readonly name: string } | null;
  /** Null for take-away, which has no covers to count. */
  readonly covers: number | null;
  readonly lines: readonly CartLine[];
  readonly discountAmount: bigint;
  /** ISO, and the order bills are listed in. */
  readonly openedAt: string;
  readonly status: "opening" | "open" | "failed";
  /**
   * Parked, per `orders.is_held`. Deliberately not a `status`, which is about
   * whether the server has heard of the bill at all: a held bill is one the
   * server knows perfectly well and the party has walked away from.
   */
  readonly isHeld: boolean;
  /**
   * Settled tax-free, per `orders.is_tax_exempt` (`ORDER_FREE_VAT`). The cart
   * prices with it so the cashier sees the tax come off before settling, not
   * on the receipt afterwards.
   */
  readonly isTaxExempt: boolean;
  /** The server's refusal, verbatim, when there was one. */
  readonly failure: string | null;
  /** False for a bill this till adopted from another terminal. */
  readonly isMine: boolean;
};

export type BillBook = {
  readonly bills: readonly Bill[];
  readonly activeKey: BillKey | null;
};

export const EMPTY_BOOK: BillBook = { bills: [], activeKey: null };

export function activeBill(book: BillBook): Bill | null {
  return book.bills.find((bill) => bill.key === book.activeKey) ?? null;
}

/** Whether a bill has anything the server has not been told about. */
export function isUnsent(bill: Bill): boolean {
  return bill.orderId === null;
}

/** A bill nothing has been ordered on, which is what may simply be released. */
export function isEmpty(bill: Bill): boolean {
  return bill.lines.length === 0;
}

/**
 * What letting this bill go is called on screen.
 *
 * A take-away bill has no table to hand back, only a bill to throw away, and
 * the table-shaped copy printed "Release ?" where the name would have gone.
 * The rule lives here so the button and the dialog that confirms it cannot
 * come to different conclusions.
 */
export function releaseLabel(
  diningStyle: Bill["diningStyle"],
): "pos.bill.clearTable" | "pos.bill.discardBill" {
  return diningStyle === "take_away" ? "pos.bill.discardBill" : "pos.bill.clearTable";
}

function replace(book: BillBook, key: BillKey, change: (bill: Bill) => Bill): BillBook {
  return { ...book, bills: book.bills.map((bill) => (bill.key === key ? change(bill) : bill)) };
}

function withActive(book: BillBook, change: (bill: Bill) => Bill): BillBook {
  return book.activeKey ? replace(book, book.activeKey, change) : book;
}

export function openBill(
  book: BillBook,
  bill: {
    key: BillKey;
    table?: { id: string; name: string } | null;
    covers?: number | null;
    openedAt: string;
  },
): BillBook {
  const opened: Bill = {
    key: bill.key,
    orderId: null,
    orderNo: null,
    // Take-away is a choice now, not the absence of one. A cashier who forgot
    // to seat a table used to ring a dine-in party as take-away and lose the
    // service charge, and nothing on screen said so.
    diningStyle: bill.table ? "dine_in" : "take_away",
    table: bill.table ?? null,
    covers: bill.table ? (bill.covers ?? 1) : null,
    lines: [],
    discountAmount: 0n,
    openedAt: bill.openedAt,
    status: "opening",
    isHeld: false,
    isTaxExempt: false,
    failure: null,
    isMine: true,
  };
  return { bills: [...book.bills, opened], activeKey: opened.key };
}

/** A bill opened on another terminal, pulled onto this one. */
export function adoptBill(
  book: BillBook,
  order: {
    id: string;
    orderNo: string;
    tableId: string | null;
    tableName: string | null;
    peopleCount: number | null;
    diningStyle: "dine_in" | "take_away";
    lines: readonly CartLine[];
    openedAt: string;
    isHeld: boolean;
    isTaxExempt: boolean;
  },
  key: BillKey,
): BillBook {
  const held = book.bills.find((bill) => bill.orderId === order.id);
  // Adopting one that is already here is a switch, not a second copy of it.
  if (held) return { ...book, activeKey: held.key };

  const adopted: Bill = {
    key,
    orderId: order.id,
    orderNo: order.orderNo,
    diningStyle: order.diningStyle,
    table: order.tableId ? { id: order.tableId, name: order.tableName ?? "" } : null,
    covers: order.peopleCount,
    // Everything the server holds has been fired by definition: it is on the
    // bill, and this till was not the one that put it there.
    lines: order.lines.map((line) => ({ ...line, isFired: true })),
    discountAmount: 0n,
    openedAt: order.openedAt,
    status: "open",
    isHeld: order.isHeld,
    isTaxExempt: order.isTaxExempt,
    failure: null,
    isMine: false,
  };
  return { bills: [...book.bills, adopted], activeKey: key };
}

/**
 * The bill learns its server id, once its create has been sent.
 *
 * Idempotent, and it never overwrites a different id: a replayed create
 * answers with the bill that already exists, which is the same one.
 */
export function learnOrderId(
  book: BillBook,
  key: BillKey,
  order: { id: string; orderNo?: string | null },
): BillBook {
  return replace(book, key, (bill) =>
    bill.orderId && bill.orderId !== order.id
      ? bill
      : { ...bill, orderId: order.id, orderNo: order.orderNo ?? bill.orderNo, status: "open" },
  );
}

/**
 * The server refused to open it.
 *
 * The lines stay. Whatever the cashier had typed is still what the customer
 * asked for, and a bill that emptied itself on a 409 would lose it.
 */
export function failBill(book: BillBook, key: BillKey, message: string): BillBook {
  return replace(book, key, (bill) => ({ ...bill, status: "failed", failure: message }));
}

export function setActive(book: BillBook, key: BillKey | null): BillBook {
  if (key !== null && !book.bills.some((bill) => bill.key === key)) return book;
  return { ...book, activeKey: key };
}

export function closeBill(book: BillBook, key: BillKey): BillBook {
  const bills = book.bills.filter((bill) => bill.key !== key);
  return {
    bills,
    activeKey: book.activeKey === key ? (bills[bills.length - 1]?.key ?? null) : book.activeKey,
  };
}

export function setLines(book: BillBook, lines: readonly CartLine[]): BillBook {
  return withActive(book, (bill) => ({ ...bill, lines }));
}

/**
 * Parking a bill, or bringing it back.
 *
 * Keyed rather than taking the active bill: the cashier resumes one from the
 * strip, and by then something else is open.
 */
export function setHeld(book: BillBook, key: BillKey, held: boolean): BillBook {
  return replace(book, key, (bill) => ({ ...bill, isHeld: held }));
}

/**
 * Replace what the station has with what the server says it has.
 *
 * A line this till fired knows its menu item, not the **order item** the server
 * created for it — and every amendment to a fired line is addressed by that id.
 * So once `/items` comes back, the fired half of the bill is taken from the
 * answer, exactly as an adopted bill is.
 *
 * Unfired lines are kept untouched: a cashier who rang the next round while the
 * first was in flight must not lose it.
 */
export function adoptServerLines(
  book: BillBook,
  orderId: string,
  lines: readonly CartLine[],
): BillBook {
  const bill = book.bills.find((row) => row.orderId === orderId);
  if (!bill) return book;
  return replace(book, bill.key, (row) => ({
    ...row,
    lines: [...lines, ...row.lines.filter((line) => !line.isFired)],
  }));
}

/** Taking the tax off a bill, or putting it back. */
export function setTaxExempt(book: BillBook, key: BillKey, exempt: boolean): BillBook {
  return replace(book, key, (bill) => ({ ...bill, isTaxExempt: exempt }));
}

export function setDiscount(book: BillBook, amount: bigint): BillBook {
  return withActive(book, (bill) => ({ ...bill, discountAmount: amount }));
}

/**
 * The party turned out to be a different size than the one seated.
 *
 * Only a bill with a table has covers: take-away is a bag, not a sitting, and
 * a count on it would land in the Covers report as a table nobody sat at.
 */
export function setCovers(book: BillBook, key: BillKey, covers: number): BillBook {
  return replace(book, key, (bill) => (bill.table ? { ...bill, covers } : bill));
}

/** Firing marks what has gone; § A prints added courses as an increment. */
export function markFired(book: BillBook, key: BillKey): BillBook {
  return replace(book, key, (bill) => ({
    ...bill,
    lines: bill.lines.map((line) => ({ ...line, isFired: true })),
  }));
}

/**
 * How to address a bill, which is the whole of the offline story.
 *
 * Once the create has been sent the id is known and the path is literal. While
 * it is still queued there is no id yet, so the operation declares what it is
 * waiting for and the queue fills it in — which works precisely because both
 * are in the queue together.
 */
export function addressOf(
  bill: Bill,
  suffix: string,
): { path: string; dependsOn?: { operationId: string; field: string; token: string } } {
  if (bill.orderId) return { path: `/store/orders/${bill.orderId}${suffix}` };
  return {
    path: `/store/orders/{{order}}${suffix}`,
    dependsOn: { operationId: bill.key, field: "id", token: "{{order}}" },
  };
}

/**
 * What a bill is called on a chip.
 *
 * Take-away has no table to name and is left to its bag glyph: the order
 * number is thirteen characters wide, and an ordinal among the open bags
 * renumbers the rest under the cashier the moment one of them settles. The
 * cart column names the bill that is actually open instead.
 */
export function labelOf(bill: Bill): string | null {
  return bill.table?.name ?? null;
}
